// Angular
import { inject, Injectable } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from "@angular/router";

// Services
import { SeoTitleState, SeoTitleStore } from "#services/seo-title/seo-title.store";

// Models
import { NestedTranslationsKeys } from "#models/translations";

// Pipes
import { TranslatePipe } from "#pipes/i18n/translate.pipe";

@Injectable({ providedIn: "root" })
export class SeoTitleStrategy extends TitleStrategy {
    private title = inject(Title);
    private i18n = inject(TranslatePipe);
    private store = inject(SeoTitleStore);

    private defaultTitle = "PromoAdminUi";
    private staticTitle?: string;
    private seoTitleKey: string | null = null;
    private state: SeoTitleState = { getTitle: null, prefix: null };

    constructor() {
        super();
        this.store.state$.subscribe((s) => {
            this.state = s;
            this.render();
        });
        this.i18n.onLanguageChange$.subscribe(() => this.render());
    }

    override updateTitle(snapshot: RouterStateSnapshot): void {
        this.staticTitle = this.buildTitle(snapshot);
        this.seoTitleKey = this.deepestData(snapshot, "seoTitleKey");
        this.render();
    }

    private render(): void {
        const { getTitle, prefix } = this.state;

        if (getTitle) {
            const name = getTitle(this.i18n.getRecordsLocaleId());
            if (name) {
                this.title.setTitle(
                    prefix
                        ? `${this.i18n.transform(prefix as NestedTranslationsKeys)} - ${name}`
                        : name
                );
                return;
            }
        }

        if (this.seoTitleKey) {
            this.title.setTitle(this.i18n.transform(this.seoTitleKey as NestedTranslationsKeys));
            return;
        }

        if (this.staticTitle) {
            this.title.setTitle(this.staticTitle);
            return;
        }

        this.title.setTitle(this.defaultTitle);
    }

    private deepestData(snapshot: RouterStateSnapshot, prop: string): string | null {
        let route: ActivatedRouteSnapshot | undefined = snapshot.root;
        let value: string | null = null;
        while (route) {
            if (route.data[prop]) {
                value = route.data[prop];
            }
            route = route.firstChild ?? undefined;
        }
        return value;
    }
}
